import moment from 'moment-timezone';
import React from 'react';

import { DisplayStarRating } from '@/components/DisplayStarRating';
import Link from '@/components/Link';
import { isReviewable } from '@/consultation/reviewWindow';
import { money } from '@/core/money';
import { formatCreditsText } from '@/utils';

import { VALUE_TAG_MAP } from './constants';
import { ExpertAvatar, ProjectChip, StatusChip, ValueTag } from './primitives';

/**
 * Normalized shape both dashboard consultation lists map their data into. The
 * Recent Consultations strip maps a raw GraphQL node; the My Experts card maps a
 * derived `ExpertConsultation` plus its parent expert.
 */
export interface ConsultationCardItem {
  id: string;
  htmlUrl?: string | null;
  /** description ?? expert_request.name ?? 'Consultation'. */
  note: string;
  date?: string | null;
  durationMinutes?: number | null;
  /** Cost in display cents (USD or OFC pip) per the `currency` prop. */
  costCents: number;
  engagementType?: string | null;
  projectName?: string | null;
  rating: number | null;
  hasReview: boolean;
  /** Whether the current viewer may review this consultation — its requester or expert (ENG-5259). */
  canReview: boolean;
  /** Value-delivered tag labels. Empty in Recent Consultations (kept lean). */
  valueTags: string[];
  expert: {
    name: string;
    firstName?: string | null;
    lastName?: string | null;
    pictureUrl?: string | null;
  };
}

interface ConsultationCardProps {
  consultation: ConsultationCardItem;
  currency: 'USD' | 'OFC';
  /** 'row' = three-column grid (default). 'compact' = stacked card for the horizontal carousel. */
  layout?: 'row' | 'compact';
}

type StatusVariant = 'needs-review' | 'scheduled' | 'completed';

function getConsultationStatus(
  c: Pick<ConsultationCardItem, 'hasReview' | 'rating' | 'engagementType'>,
  canShowReview: boolean
): { variant: StatusVariant; label: string } {
  // A consultation only counts as needs-review when it's reviewable: still inside
  // the 30-day window (isReviewable) AND the viewer is its requester/expert
  // (ENG-5259). Otherwise it reads as completed.
  if (!c.hasReview && c.rating === null && canShowReview) {
    return { variant: 'needs-review', label: 'Needs review' };
  }
  if (c.engagementType === 'confirmed') return { variant: 'scheduled', label: 'Scheduled' };
  return { variant: 'completed', label: 'Completed' };
}

const STATUS_CHIP_VARIANT: Record<StatusVariant, 'suggested' | 'vetting' | 'active'> = {
  'needs-review': 'vetting',
  scheduled: 'suggested',
  completed: 'active',
};

function formatDuration(minutes: number | null | undefined): string | null {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes) || minutes <= 0) {
    return null;
  }
  if (minutes < 60) return `${minutes} min`;
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
}

export function ConsultationCard({
  consultation,
  currency,
  layout = 'row',
}: ConsultationCardProps): React.ReactNode {
  const { expert } = consultation;
  // ENG-5259: only the consultation's own reviewer (requester or expert) may be
  // nudged — never an account manager or other viewer. ENG-5255: only in-window.
  const canShowReview = isReviewable(consultation.date) && consultation.canReview;
  const status = getConsultationStatus(consultation, canShowReview);
  const date = consultation.date ? moment(consultation.date).format('MMM D, YYYY') : null;
  const duration = formatDuration(consultation.durationMinutes);
  const engagementLabel =
    consultation.engagementType === 'written_response' ? 'Written response' : 'call';
  const cost =
    consultation.costCents > 0
      ? currency === 'OFC'
        ? formatCreditsText(consultation.costCents)
        : money(consultation.costCents, currency).formatted({ omitEmptyCents: true })
      : null;
  const metaParts = [date, duration ? `${duration} ${engagementLabel}` : null, cost].filter(
    Boolean
  );

  const ratingOrCta =
    consultation.rating !== null ? (
      <DisplayStarRating rating={consultation.rating} size={14} />
    ) : !consultation.hasReview && canShowReview ? (
      <Link
        to={consultation.htmlUrl ? `${consultation.htmlUrl}?review_call=true` : '/consultations'}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        className="inline-flex items-center whitespace-nowrap rounded-md bg-yellow-500 px-10 py-4 text-[1.1rem] font-semibold text-white no-underline shadow-5 transition-shadow hover:shadow-1"
      >
        Leave a Review
      </Link>
    ) : null;

  const avatar = (
    <ExpertAvatar
      firstName={expert.firstName}
      lastName={expert.lastName}
      name={expert.name}
      pictureUrl={expert.pictureUrl}
      size={40}
    />
  );

  const content = (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
        <span className="text-14 font-semibold text-on-surface">{expert.name}</span>
        <StatusChip variant={STATUS_CHIP_VARIANT[status.variant]} label={status.label} />
      </div>

      <p className="m-[0] line-clamp-3 break-words text-14 text-on-surface">{consultation.note}</p>

      {(metaParts.length > 0 || consultation.projectName) && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-4 text-12 text-on-surface-muted">
          {metaParts.length > 0 && <span>{metaParts.join(' · ')}</span>}
          {consultation.projectName && <ProjectChip name={consultation.projectName} />}
        </div>
      )}

      {consultation.valueTags.length > 0 && (
        <div className="flex flex-wrap gap-4">
          {consultation.valueTags.map((tag) => (
            <ValueTag key={tag} type={VALUE_TAG_MAP[tag] ?? 'time-savings'} count={1} />
          ))}
        </div>
      )}
    </div>
  );

  // A needs-review card's action is its "Leave a Review" CTA; don't also wrap the
  // whole card in a link, which would nest an <a> inside an <a>. Reviewed and
  // other cards link to the consultation.
  const isCardLink = !!consultation.htmlUrl && status.variant !== 'needs-review';
  const base = 'rounded-md border border-surface-border bg-surface-card p-12 transition-colors';
  const hover = isCardLink ? 'hover:bg-surface-secondary' : '';

  const card =
    layout === 'compact' ? (
      <div
        className={`${base} ${hover} flex min-w-[20rem] shrink-0 snap-start flex-col gap-8`}
        role="listitem"
      >
        <div className="flex items-start gap-8">
          {avatar}
          {content}
        </div>
        {ratingOrCta && <div className="shrink-0">{ratingOrCta}</div>}
      </div>
    ) : (
      <div
        className={`${base} ${hover} grid grid-cols-[auto_minmax(0,_1fr)_auto] items-start gap-x-12 gap-y-8`}
        role="listitem"
      >
        {avatar}
        {content}
        {ratingOrCta && <div className="shrink-0">{ratingOrCta}</div>}
      </div>
    );

  if (consultation.htmlUrl && status.variant !== 'needs-review') {
    return (
      <Link to={consultation.htmlUrl} className="no-underline">
        {card}
      </Link>
    );
  }

  return card;
}
